import { z } from "zod";
import { GAMES } from "../domain/games";

/**
 * «برترین‌ها» — the public wins leaderboard.
 *
 * ── WHAT IT RANKS, AND WHY IT IS NOT ELO ──────────────────────────────────────────────────────
 * WINS, over ONLINE matches only. Elo was measured and rejected: a solo game is played against
 * bots, every bot is scored at a flat `ELO_SEED` whatever its difficulty, and solo results used to
 * move the rating — so on prod 2026-09-10 the share of the current top-50's rating GAIN won
 * against bots read بی‌دل 80.5% · تخته نرد 50.7% · منچ 35.8% · حکم 10.5% · دوز 0.4% · شطرنج 0.0%.
 * An Elo board would have shipped a بی‌دل ranking that was four-fifths bot-farming.
 *
 * ⚠️ ONLINE-ONLY IS ENFORCED BY `MatchHistory.mode`, WHICH IS THREE-VALUED. NULL means the row
 * predates the column and is EXCLUDED — never counted as online. So «همیشه» ranks from the day
 * tracking began, not from the first match ever played.
 */

/** The three period tabs. `all` is "everything we can attribute", not "everything that happened". */
export const LEADERBOARD_PERIODS = ["week", "month", "all"] as const;
export type LeaderboardPeriod = (typeof LEADERBOARD_PERIODS)[number];

/** The game filter: one game, or «همه» across every ACTIVE game. */
export const LEADERBOARD_ALL_GAMES = "all";

/** How many ranked rows the board serves. The podium takes the first three of these. */
export const LEADERBOARD_LIMIT = 50;

/**
 * One ranked player.
 *
 * Carries the same name/avatar fields the rest of the app resolves a person from (`displayNameOf`
 * + `GET /avatar/:id`), and — like every other public view — NEVER a Telegram identifier.
 */
export const leaderboardRowSchema = z.object({
  /** 1-based. Ties share a rank (`rank()`, not `row_number()`), so ranks can repeat and skip. */
  rank: z.number().int().min(1),
  userId: z.string(),
  appUsername: z.string().nullable(),
  nickname: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  avatarId: z.string(),
  avatarPhotoId: z.string().nullable(),
  isOfficial: z.boolean().default(false),
  /** Online wins inside the period. The single number the row shows. */
  wins: z.number().int().min(0),
});
export type LeaderboardRow = z.infer<typeof leaderboardRowSchema>;

/**
 * The caller's own standing — the pinned row.
 *
 * ⚠️ SHOWN EVEN WHEN THE CALLER IS ABSENT FROM `rows` — which since 2026-09-14 means only that
 * they are outside the top {@link LEADERBOARD_LIMIT}, not that anything withheld them. The pinned
 * row is private to them and the app's existing rule is that the owner always sees their own
 * numbers, so a player at rank 63 still learns where they stand.
 */
export const leaderboardMeSchema = z.object({
  userId: z.string(),
  /** Null when the caller has NO online win in this period — unranked, not rank 0. */
  rank: z.number().int().min(1).nullable(),
  wins: z.number().int().min(0),
  /**
   * Wins needed to reach the next rank ABOVE. Null at rank 1 and when unranked. Zero is
   * impossible by construction: a tie shares a rank, so the row above always has strictly more.
   */
  winsToNextRank: z.number().int().min(1).nullable(),
  /**
   * THE CROSS-PERIOD HINT — the nearest WIDER period in which this caller DOES have a win, when
   * the selected one has none. «این هفته بردی نداشتی» is a dead end; «ولی در همیشه ۴ برد داری» is
   * a reason to play. Null when the selected period has a win (there is nothing to redirect to),
   * and null when no wider period has one either (a genuinely new player, who gets the plain
   * sentence). The game filter travels with it, so the hint never counts a game the viewer is not
   * looking at.
   */
  elsewhere: z
    .object({
      period: z.enum(LEADERBOARD_PERIODS),
      wins: z.number().int().min(1),
    })
    .nullable()
    .default(null),
});
export type LeaderboardMe = z.infer<typeof leaderboardMeSchema>;

/** `GET /leaderboard?period=&game=` */
export const leaderboardResponseSchema = z.object({
  period: z.enum(LEADERBOARD_PERIODS),
  /** The echoed filter — one game id, or «همه». */
  game: z.union([z.enum(GAMES), z.literal(LEADERBOARD_ALL_GAMES)]),
  rows: z.array(leaderboardRowSchema),
  /** Null only if the caller somehow has no user row; normally always present. */
  me: leaderboardMeSchema.nullable(),
});
export type LeaderboardResponse = z.infer<typeof leaderboardResponseSchema>;

/** Query params for `GET /leaderboard`. Both optional — the defaults are the screen's defaults. */
export const leaderboardQuerySchema = z.object({
  period: z.enum(LEADERBOARD_PERIODS).default("week"),
  game: z.union([z.enum(GAMES), z.literal(LEADERBOARD_ALL_GAMES)]).default(LEADERBOARD_ALL_GAMES),
});
export type LeaderboardQuery = z.infer<typeof leaderboardQuerySchema>;
